import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/Tabs';
import { Textarea } from '../../../components/ui/Textarea';
import { usePageTitle } from '../../../hooks/shared';
import { useAuthStore } from '../../identity/store/authStore';
import { adminAPI } from '../api/admin';
import { authoritiesAPI } from '../api/authorities';
import USCoverageMap from '../components/USCoverageMap';
import { useAuthorityCheckStore } from '../store/authorityCheckStore';
import { useWebsiteFinderStore } from '../store/websiteFinderStore';

const SCRAPER_STYLES = {
  working: 'bg-emerald-100 text-emerald-700',
  broken: 'bg-red-100 text-red-700',
  not_configured: 'bg-neutral-100 text-neutral-400',
  unknown: 'bg-neutral-100 text-neutral-400',
  not_run: 'bg-sky-100 text-sky-600',
};

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  broken: 'bg-red-100 text-red-700',
  not_found: 'bg-orange-100 text-orange-700',
  unknown: 'bg-neutral-100 text-neutral-500',
};

const TABS = [
  { key: 'map', label: 'Coverage Map' },
  { key: 'states', label: 'States & Territories' },
  { key: 'counties', label: 'Counties' },
];

// ── Edit modal ────────────────────────────────────────────────────────────────

function EditModal({ authority, onClose }) {
  const [websiteUrl, setWebsiteUrl] = useState(authority.websiteUrl ?? '');
  const [calendarUrl, setCalendarUrl] = useState(authority.calendarUrl ?? '');
  const [notes, setNotes] = useState(authority.notes ?? '');
  const [error, setError] = useState('');
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data) => authoritiesAPI.update(authority.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-authorities'] });
      onClose();
    },
    onError: (e) => setError(e.response?.data?.message ?? 'Failed to save'),
  });

  function handleSave() {
    setError('');
    if (websiteUrl) {
      try {
        new URL(websiteUrl);
      } catch {
        setError('Invalid website URL (include https://)');
        return;
      }
    }
    if (calendarUrl) {
      try {
        new URL(calendarUrl);
      } catch {
        setError('Invalid calendar URL (include https://)');
        return;
      }
    }
    mutation.mutate({
      websiteUrl: websiteUrl || null,
      calendarUrl: calendarUrl || null,
      notes: notes || null,
    });
  }

  return (
    <Dialog open={!!authority} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit authority</DialogTitle>
        </DialogHeader>
        <p className="text-base text-muted-foreground">
          {authority.name} ({authority.state})
        </p>
        <div className="space-y-4">
          <div>
            <Label htmlFor="websiteUrl">General Website URL</Label>
            <Input
              id="websiteUrl"
              type="url"
              value={websiteUrl}
              onChange={(e) => {
                setWebsiteUrl(e.target.value);
                setError('');
              }}
              placeholder="https://county.gov/"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="calendarUrl">Election Calendar URL</Label>
            <Input
              id="calendarUrl"
              type="url"
              value={calendarUrl}
              onChange={(e) => {
                setCalendarUrl(e.target.value);
                setError('');
              }}
              placeholder="https://vote.county.gov/"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
          {error && <p className="text-base text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Discover candidates modal ─────────────────────────────────────────────────

function DiscoverModal({ authority, candidates, wikiSite, onClose }) {
  const qc = useQueryClient();

  // Build deduplicated option list: wikiSite first (tagged), then Brave candidates
  const options = [];
  if (wikiSite) {
    options.push({
      url: wikiSite,
      title: 'Official website (Wikipedia)',
      score: null,
      isWiki: true,
    });
  }
  for (const c of candidates) {
    if (c.url !== wikiSite) options.push(c);
  }

  const [selected, setSelected] = useState(options[0]?.url ?? '');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (url) => authoritiesAPI.update(authority.id, { websiteUrl: url }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-authorities'] });
      onClose();
    },
    onError: (e) => setError(e.response?.data?.message ?? 'Failed to save'),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Discover website — {authority.name} ({authority.state})
          </DialogTitle>
        </DialogHeader>

        {options.length === 0 ? (
          <p className="text-base text-neutral-500 py-4 text-center">
            No candidates found. Try editing the URL manually.
          </p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {options.map((opt) => (
              <label
                key={opt.url}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selected === opt.url
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <input
                  type="radio"
                  name="discover-url"
                  value={opt.url}
                  checked={selected === opt.url}
                  onChange={() => setSelected(opt.url)}
                  className="mt-0.5 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-neutral-800 break-all">
                      {opt.url}
                    </span>
                    {opt.isWiki && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded shrink-0">
                        Wikipedia
                      </span>
                    )}
                    {opt.score != null && (
                      <span className="text-xs text-neutral-400 shrink-0">score {opt.score}</span>
                    )}
                    <a
                      href={opt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-neutral-400 hover:text-primary-600 shrink-0 ml-auto"
                    >
                      ↗
                    </a>
                  </div>
                  {opt.title && opt.title !== opt.url && !opt.isWiki && (
                    <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{opt.title}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {error && <p className="text-base text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {options.length > 0 && (
            <Button
              onClick={() => mutation.mutate(selected)}
              disabled={!selected || mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Set as website URL'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Authority table (shared by States and Counties tabs) ──────────────────────

function AuthorityTable({ typeFilter, typeNotFilter, onStateClick }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabKey = typeFilter === 'state' ? 'states' : 'counties';
  const search = searchParams.get(`${tabKey}_q`) ?? '';
  const stateFilter = searchParams.get(`${tabKey}_state`) ?? '';
  const page = parseInt(searchParams.get(`${tabKey}_page`) ?? '1', 10);

  const [editing, setEditing] = useState(null);
  const [checking, setChecking] = useState(null);
  const [running, setRunning] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [discovering, setDiscovering] = useState(null); // authorityId being discovered
  const [discoverResult, setDiscoverResult] = useState(null); // { authority, candidates, wikiSite }

  const discoveringAll = useWebsiteFinderStore((s) => s.running);
  const discoverAllProcessed = useWebsiteFinderStore((s) => s.processed);
  const discoverAllTotal = useWebsiteFinderStore((s) => s.total);
  const startDiscoverAll = useWebsiteFinderStore((s) => s.start);
  const stopDiscoverAll = useWebsiteFinderStore((s) => s.stop);

  // Use global store for check-all and run-all operations
  const checkingAll = useAuthorityCheckStore((s) => s.checkingAll);
  const checkAllProgress = useAuthorityCheckStore((s) => s.checkAllProgress);
  const runningAll = useAuthorityCheckStore((s) => s.runningAll);
  const runAllProgress = useAuthorityCheckStore((s) => s.runAllProgress);
  const checkAll = useAuthorityCheckStore((s) => s.checkAll);
  const runAll = useAuthorityCheckStore((s) => s.runAll);
  const abortCheckAll = useAuthorityCheckStore((s) => s.abortCheckAll);
  const abortRunAll = useAuthorityCheckStore((s) => s.abortRunAll);

  function setParam(key, value) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value) next.delete(`${tabKey}_${key}`);
        else next.set(`${tabKey}_${key}`, value);
        next.delete(`${tabKey}_page`);
        return next;
      },
      { replace: true }
    );
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-authorities', tabKey, search, stateFilter, page],
    queryFn: () =>
      authoritiesAPI.list({
        search: search || undefined,
        state: stateFilter || undefined,
        type: typeFilter || undefined,
        typeNot: typeNotFilter || undefined,
        page,
        limit: 50,
      }),
    keepPreviousData: true,
  });

  async function handleCheck(authority) {
    setChecking(authority.id);
    try {
      await authoritiesAPI.triggerCheck(authority.id);
      qc.invalidateQueries({ queryKey: ['admin-authorities', tabKey] });
    } catch {
      // error shown via re-fetch
    } finally {
      setChecking(null);
    }
  }

  async function handleRun(authority) {
    setRunning(authority.id);
    try {
      await authoritiesAPI.runScraper(authority.id);
      qc.invalidateQueries({ queryKey: ['admin-authorities', tabKey] });
    } catch {
      // error shown via re-fetch
    } finally {
      setRunning(null);
    }
  }

  async function handleDiscover(authority) {
    setDiscovering(authority.id);
    try {
      const result = await authoritiesAPI.discoverWebsite(authority.id);
      setDiscoverResult({
        authority,
        candidates: result.candidates ?? [],
        wikiSite: result.wikiSite ?? null,
      });
    } catch {
      // silently fail — user can try again or edit manually
    } finally {
      setDiscovering(null);
    }
  }

  function handleDiscoverAll() {
    if (discoveringAll) {
      stopDiscoverAll();
    } else {
      startDiscoverAll({ state: stateFilter || undefined, limit: 500, delayMs: 1500 });
    }
  }

  async function handleCheckAll() {
    if (checkingAll) {
      abortCheckAll();
    } else {
      checkAll(typeFilter, typeNotFilter, qc, tabKey);
    }
  }

  async function handleRunAll() {
    if (runningAll) {
      abortRunAll();
    } else {
      runAll(typeFilter, typeNotFilter, qc, tabKey);
    }
  }

  const authorities = data?.authorities ?? [];
  const pagination = data?.pagination ?? {};

  // Determine status for each authority
  function getStatus(a) {
    if (!a.calendarUrl) return 'no_url';
    if (a.websiteStatus === 'broken') return 'broken';
    if (a.websiteStatus === 'not_found') return 'missing_calendar';
    if (
      a.websiteStatus === 'active' &&
      a.lastCheckedAt &&
      Date.now() - new Date(a.lastCheckedAt) > 30 * 24 * 60 * 60 * 1000
    )
      return 'stale';
    if (a.websiteStatus === 'active') return 'verified';
    return 'needs_review';
  }

  // Aggregate stats (for counties tab)
  const stats = typeNotFilter
    ? {
        total: pagination.total ?? 0,
        verified: authorities.filter((a) => getStatus(a) === 'verified').length,
        needsReview: authorities.filter((a) => getStatus(a) === 'needs_review').length,
        missing: authorities.filter((a) => getStatus(a) === 'missing_calendar').length,
        broken: authorities.filter((a) => getStatus(a) === 'broken').length,
      }
    : null;

  const statusColors = {
    verified: 'bg-emerald-100 text-emerald-700',
    needs_review: 'bg-amber-100 text-amber-700',
    missing_calendar: 'bg-orange-100 text-orange-700',
    broken: 'bg-red-100 text-red-700',
    stale: 'bg-blue-100 text-blue-700',
    no_url: 'bg-neutral-100 text-neutral-500',
  };

  const statusLabels = {
    verified: 'Verified',
    needs_review: 'Needs Review',
    missing_calendar: 'Missing Calendar',
    broken: 'Broken',
    stale: 'Stale',
    no_url: 'No URL',
  };

  return (
    <>
      {editing && <EditModal authority={editing} onClose={() => setEditing(null)} />}
      {discoverResult && (
        <DiscoverModal
          authority={discoverResult.authority}
          candidates={discoverResult.candidates}
          wikiSite={discoverResult.wikiSite}
          onClose={() => setDiscoverResult(null)}
        />
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setParam('q', e.target.value)}
              placeholder="Search jurisdictions…"
              className="w-full pl-4 pr-4 py-2 border border-neutral-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          {/* State filter only makes sense on counties tab */}
          {typeNotFilter && (
            <input
              type="text"
              value={stateFilter}
              onChange={(e) => setParam('state', e.target.value.toUpperCase().slice(0, 2))}
              placeholder="State ('AK')"
              maxLength={2}
              className="w-50 px-3 py-2 border border-neutral-200 rounded-lg text-base uppercase focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          )}
        </div>
      </div>

      {/* Aggregate stats for counties tab */}
      {!isLoading && stats && (
        <div className="mb-4 p-4 bg-white rounded-xl border border-neutral-200 shadow-sm">
          <div className="flex flex-wrap gap-4 items-center text-base">
            <div className="font-medium text-neutral-900">
              {stats.total.toLocaleString()} jurisdictions
            </div>
            <div className="flex gap-3 flex-wrap text-xs">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-600" />
                <span>
                  <strong>{stats.verified}</strong> Verified
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-600" />
                <span>
                  <strong>{stats.needsReview}</strong> Needs Review
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-600" />
                <span>
                  <strong>{stats.missing}</strong> Missing Calendar
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-600" />
                <span>
                  <strong>{stats.broken}</strong> Broken
                </span>
              </div>
            </div>
            {stats.total > 0 && (
              <div className="text-xs text-neutral-500 ml-auto">
                {((stats.verified / stats.total) * 100).toFixed(0)}% coverage
              </div>
            )}
          </div>
        </div>
      )}

      {isLoading && <div className="text-center py-16 text-neutral-400">Loading…</div>}

      {!isLoading && (
        <>
          {pagination.total != null && !stats && (
            <p className="text-base text-neutral-500 mb-3">
              {pagination.total.toLocaleString()} jurisdictions
            </p>
          )}

          <div className="bg-white rounded-2xl border border-neutral-200 overflow-x-auto shadow-sm">
            <table className="w-full text-base min-w-max">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  {typeNotFilter && <th className="w-8 px-4 py-3" />}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden sm:table-cell">
                    <div className="flex items-center gap-2 justify-between">
                      <span>Website</span>
                      {typeNotFilter && (
                        <button
                          onClick={handleDiscoverAll}
                          className={`px-2 py-0.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                            discoveringAll
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-violet-600 text-white hover:bg-violet-700'
                          }`}
                        >
                          {discoveringAll
                            ? `Cancel (${discoverAllProcessed}/${discoverAllTotal})`
                            : 'Discover All'}
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Website Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden lg:table-cell">
                    <div className="flex items-center gap-2 justify-between">
                      <span>Last Checked</span>
                      <button
                        onClick={handleCheckAll}
                        className={`px-2 py-0.5 text-xs font-medium rounded transition-colors whitespace-nowrap ml-auto ${
                          checkingAll
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-amber-600 text-white hover:bg-amber-700'
                        }`}
                      >
                        {checkingAll
                          ? `Cancel (${checkAllProgress.done}/${checkAllProgress.total})`
                          : 'Check All'}
                      </button>
                    </div>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden md:table-cell">
                    Election Calendar
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden lg:table-cell">
                    Scraper
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide hidden xl:table-cell">
                    Last Election
                  </th>
                  <th className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={handleRunAll}
                        className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
                          runningAll
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-sky-600 text-white hover:bg-sky-700'
                        }`}
                      >
                        {runningAll
                          ? `Cancel (${runAllProgress.done}/${runAllProgress.total})`
                          : 'Run All'}
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {authorities.map((a) => {
                  const isExpanded = expanded === a.id;
                  const status = getStatus(a);
                  return (
                    <>
                      <tr
                        key={a.id}
                        className={`hover:bg-neutral-50 ${typeFilter === 'state' ? 'cursor-pointer' : 'cursor-default'}`}
                        onClick={() => typeFilter === 'state' && onStateClick(a.state)}
                      >
                        {typeNotFilter && (
                          <td className="px-4 py-3 w-8">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(isExpanded ? null : a.id);
                              }}
                              className="text-neutral-400 hover:text-neutral-600"
                            >
                              <svg
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="font-medium text-neutral-900">{a.name}</div>
                          <div className="text-xs text-neutral-400">
                            {a.state} · {a.jurisdictionType}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell max-w-xs">
                          {a.websiteUrl ? (
                            <a
                              href={a.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-600 hover:text-primary-700 truncate block"
                            >
                              {a.websiteUrl}
                            </a>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDiscover(a);
                              }}
                              disabled={discovering === a.id}
                              className="text-xs text-violet-600 hover:text-violet-700 disabled:opacity-50 italic"
                            >
                              {discovering === a.id ? 'Searching…' : 'Discover →'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[a.websiteStatus] ?? 'bg-neutral-100 text-neutral-400'}`}
                          >
                            {a.websiteStatus ?? 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-neutral-400 hidden lg:table-cell">
                          {a.lastCheckedAt ? new Date(a.lastCheckedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell max-w-xs">
                          {a.calendarUrl ? (
                            <a
                              href={a.calendarUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary-600 hover:text-primary-700 truncate block"
                            >
                              {a.calendarUrl}
                            </a>
                          ) : (
                            <a
                              href={`https://duckduckgo.com/?q=${encodeURIComponent(`!ducky election dates calendar ${a.name} ${a.state} `)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 italic text-xs text-neutral-700 hover:text-primary-600 transition-colors py-1"
                            >
                              Search
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {a.scraperKey ? (
                            <div className="flex flex-col gap-0.5">
                              <Link
                                to={`/admin/authorities/${a.id}`}
                                className={`px-2 py-0.5 text-xs font-medium rounded-full hover:opacity-75 transition-opacity w-fit ${
                                  SCRAPER_STYLES[a.scraperStatus] ?? SCRAPER_STYLES.not_run
                                }`}
                              >
                                {a.scraperKey}
                              </Link>
                            </div>
                          ) : (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${SCRAPER_STYLES[a.scraperStatus] ?? 'bg-neutral-100 text-neutral-400'}`}
                            >
                              {a.scraperStatus ?? '—'}
                            </span>
                          )}
                        </td>
                        <td
                          className="px-4 py-3 hidden xl:table-cell"
                          title={
                            a.nextElectionFallback
                              ? 'Hardcoded fallback data'
                              : (a.nextElectionName ?? undefined)
                          }
                        >
                          {a.nextElectionFallback ? (
                            <span className="text-xs italic text-neutral-400">fallback</span>
                          ) : (
                            <span className="text-xs text-neutral-500">
                              {a.nextElectionName ? a.nextElectionName.slice(0, 15) : '—'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 w-52" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => setEditing(a)}
                              className="text-xs px-2.5 py-1 w-10 border border-neutral-200 rounded-lg hover:bg-neutral-50 text-neutral-600"
                            >
                              Edit
                            </button>
                            {!a.websiteUrl && (
                              <button
                                onClick={() => handleDiscover(a)}
                                disabled={discovering === a.id}
                                className="text-xs py-1 px-2.5 border border-violet-200 rounded-lg hover:bg-violet-50 text-violet-600 disabled:opacity-50"
                              >
                                {discovering === a.id ? 'Searching…' : 'Discover'}
                              </button>
                            )}
                            {a.calendarUrl && (
                              <button
                                onClick={() => handleCheck(a)}
                                disabled={checking === a.id}
                                className="text-xs py-1 w-22 text-center border border-neutral-200 rounded-lg hover:bg-neutral-50 text-neutral-600 disabled:opacity-50"
                              >
                                {checking === a.id ? 'Checking…' : 'Check'}
                              </button>
                            )}
                            {(a.scraperKey || a.websiteUrl) && (
                              <button
                                onClick={() => handleRun(a)}
                                disabled={running === a.id}
                                title={a.scraperKey ? undefined : 'General scraper'}
                                className="text-xs py-1 w-18 text-center border border-sky-200 rounded-lg hover:bg-sky-50 text-sky-600 disabled:opacity-50"
                              >
                                {running === a.id ? 'Running…' : 'Run'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded detail panel for counties */}
                      {typeNotFilter && isExpanded && (
                        <tr className="bg-neutral-50 border-t-0">
                          <td colSpan={typeNotFilter ? 10 : 9} className="px-4 py-4">
                            <div className="grid grid-cols-2 gap-6 text-base">
                              <div>
                                <h4 className="font-semibold text-neutral-900 mb-2">
                                  Authority Info
                                </h4>
                                <div className="space-y-1 text-neutral-600">
                                  <div>
                                    <span className="font-medium">Type:</span> {a.jurisdictionType}
                                  </div>
                                  <div>
                                    <span className="font-medium">State:</span> {a.state}
                                  </div>
                                  {a.notes && (
                                    <div>
                                      <span className="font-medium">Notes:</span> {a.notes}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold text-neutral-900 mb-2">
                                  Discovery Status
                                </h4>
                                <div className="space-y-1 text-neutral-600">
                                  <div>
                                    <span className="font-medium">Website Status:</span>{' '}
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[a.websiteStatus]}`}
                                    >
                                      {a.websiteStatus}
                                    </span>
                                  </div>
                                  {a.lastCheckedAt && (
                                    <div>
                                      <span className="font-medium">Last Checked:</span>{' '}
                                      {new Date(a.lastCheckedAt).toLocaleDateString()}
                                    </div>
                                  )}
                                  {a.websiteUrl && (
                                    <div>
                                      <span className="font-medium">Website:</span>{' '}
                                      <a
                                        href={a.websiteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:underline truncate"
                                      >
                                        {a.websiteUrl}
                                      </a>
                                    </div>
                                  )}
                                  {a.calendarUrl && (
                                    <div>
                                      <span className="font-medium">Calendar:</span>{' '}
                                      <a
                                        href={a.calendarUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:underline truncate"
                                      >
                                        {a.calendarUrl}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>

            {authorities.length === 0 && (
              <div className="text-center py-12 text-neutral-400">No jurisdictions found.</div>
            )}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
                className="px-3 py-1.5 text-base border border-neutral-200 rounded-lg disabled:opacity-40 hover:bg-neutral-50"
              >
                Previous
              </button>
              <span className="text-base text-neutral-500">
                Page {page} of {pagination.pages}
              </span>
              <button
                disabled={page >= pagination.pages}
                onClick={() => setParam('page', String(page + 1))}
                className="px-3 py-1.5 text-base border border-neutral-200 rounded-lg disabled:opacity-40 hover:bg-neutral-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminAuthorities() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const checkingAll = useAuthorityCheckStore((s) => s.checkingAll);
  const checkAllProgress = useAuthorityCheckStore((s) => s.checkAllProgress);
  const runningAll = useAuthorityCheckStore((s) => s.runningAll);
  const runAllProgress = useAuthorityCheckStore((s) => s.runAllProgress);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'map';

  usePageTitle('Admin — Authority Editor');

  if (user && user.role !== 'admin') {
    navigate('/');
    return null;
  }

  function setTab(key) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true }
    );
  }

  const { data: mapData } = useQuery({
    queryKey: ['admin-coverage-map'],
    queryFn: adminAPI.getCoverageMap,
    staleTime: 5 * 60 * 1000,
  });

  function handleMapStateClick(code) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'counties');
        if (code) next.set('counties_state', code);
        else next.delete('counties_state');
        next.delete('counties_page');
        return next;
      },
      { replace: true }
    );
  }

  const countiesStateFilter = searchParams.get('counties_state') ?? '';

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className="container-custom">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {/* Header + tabs inline */}
          <div className="flex items-center justify-between mb-6 gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="shrink-0">
                <h1 className="text-2xl font-serif font-bold leading-tight">Authority Editor</h1>
                <p className="text-neutral-500 text-base mt-0.5">
                  Edit website URLs, scraper configs, and trigger checks
                </p>
              </div>
              <TabsList className="shrink-0">
                {TABS.map((t) => (
                  <TabsTrigger key={t.key} value={t.key} className="relative">
                    {t.label}
                    {(checkingAll || runningAll) &&
                      (t.key === 'states' || t.key === 'counties') && (
                        <svg
                          className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 text-amber-600 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            opacity="0.25"
                          />
                          <path
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <button
              onClick={() => navigate('/admin/submissions')}
              className="text-base text-primary-600 hover:text-primary-700 font-medium shrink-0"
            >
              Submissions queue →
            </button>
          </div>
          {/* Tab: Coverage Map */}
          <TabsContent value="map">
            <div
              className="bg-white rounded-2xl border border-neutral-200 p-4 shadow-sm flex flex-col"
              style={{ height: 'calc(100vh - 14rem)' }}
            >
              <div className="flex items-center justify-between mb-2 shrink-0">
                <h2 className="text-base font-semibold text-neutral-700">Coverage Map</h2>
                {countiesStateFilter && (
                  <button
                    onClick={() => handleMapStateClick('')}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Clear filter ({countiesStateFilter}) ×
                  </button>
                )}
              </div>
              <div className="flex-1 min-h-0">
                <USCoverageMap
                  mapData={mapData}
                  activeState={countiesStateFilter || null}
                  onStateClick={(code) =>
                    handleMapStateClick(code === countiesStateFilter ? '' : code)
                  }
                />
              </div>
            </div>
          </TabsContent>

          {/* Tab: States & Territories */}
          <TabsContent value="states">
            <AuthorityTable
              typeFilter="state"
              onStateClick={(state) => handleMapStateClick(state)}
            />
          </TabsContent>

          {/* Tab: Counties */}
          <TabsContent value="counties">
            <AuthorityTable
              typeNotFilter="state"
              onStateClick={(state) => handleMapStateClick(state)}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
